import React, { useMemo } from 'react';
import { ChevronUpIcon } from '@heroicons/react/24/outline';
import Typography from '~/components/Typography';
import Button from '~/components/Button';
import { useDroppable } from '@dnd-kit/core';
import useFormulaContext from '../../pages/FinancialModel/useFormulaContext';
import useTableContext from './hooks/useTableContext';

const GroupRow = ({
  uuid,
  name,
  isCollapsed,
  children,
}: {
  uuid: string;
  name: string;
  isCollapsed: boolean;
  children: React.ReactNode[];
}): React.ReactNode => {
  const { setNodeRef } = useDroppable({
    id: uuid,
  });
  const { toggleGroupCollapse } = useFormulaContext();
  const { columnWidths, draggingOverId } = useTableContext();

  const isDraggingOverThisGroup = draggingOverId === uuid;

  let backgroundColor = 'bg-white';
  if (isDraggingOverThisGroup) {
    backgroundColor = !isCollapsed ? 'bg-green-50' : 'bg-green-50';
  }

  // +25 to account for the drag handle
  const fullWidth = useMemo(() => columnWidths.reduce((acc, curr) => acc + curr, 0) + 25, [columnWidths]);

  return (
    <div style={{ width: `${fullWidth}px`, minWidth: `${fullWidth}px` }}>
      <div
        ref={setNodeRef}
        className={`mt-1.5 border-t border-neutral-50 w-full flex items-center group gap-5 border-b pl-3 py-[9px] ${backgroundColor}`}
      >
        <button
          className="flex items-center bg-neutral-25 rounded-full py-[5px] px-3 hover:bg-green-25 hover:text-green-500 gap-1"
          onClick={() => toggleGroupCollapse(uuid)}
        >
          <ChevronUpIcon className={`text-neutral-300 stroke-2 size-4 ${!isCollapsed ? 'rotate-180' : 'rotate-90'}`} />
          <Typography size="xs" weight="medium" color="secondary">
            {name}
          </Typography>
          <div className="flex items-center justify-center">
            <Typography size="xs" color="lightGray" className="!no-underline">
              {children.length}
            </Typography>
          </div>
        </button>
        <Button fill="clear" className="!w-auto hidden group-hover:block !p-0">
          New Attribute
        </Button>
      </div>
      {!isCollapsed ? children : null}
    </div>
  );
};

export default GroupRow;
